import { useEffect, useState, type ReactNode } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, CircularProgress, Box, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import { useApi } from '../../../hooks/useApi';
import type { Enrolment, EnrolmentHistoryEntry, EnrolmentStatus } from '../../../types/alertEnrolment';
import { NETWORK_COLUMNS, STATUS_COLORS, STATUS_OPTIONS_TRANSFER } from './enrolmentConstants';
import { BRAND_LABEL } from '../../../constants/brand';

const NETWORK_LABEL: Record<string, string> = Object.fromEntries(NETWORK_COLUMNS.map((n) => [n.network, n.label]));
const BOOL_FIELD_LABEL: Record<string, string> = { dashboard_access: 'dashboard access', representment: 'representment' };
const STATUS_LABEL: Record<string, string> = Object.fromEntries(STATUS_OPTIONS_TRANSFER.map((o) => [o.value, o.label]));

// Chargemont staff show as just their username; automated changes show as "System".
function formatActor(name: string | null): string {
  if (!name || name === 'system') return 'System';
  return name.endsWith('@chargemont.com') ? name.slice(0, -'@chargemont.com'.length) : name;
}

// Timeline node color per event — status changes borrow the new status's own color.
function actionColor(h: EnrolmentHistoryEntry): string {
  switch (h.action) {
    case 'created': return '#16a34a';
    case 'status_changed': return STATUS_COLORS[h.newValue as EnrolmentStatus]?.main ?? '#2563eb';
    case 'note_updated': return '#d97706';
    case 'updated': return '#7c3aed';
    case 'verified': return '#0d9488';
    default: return '#64748b';
  }
}

function relativeTime(iso: string): string {
  const now = dayjs();
  const then = dayjs(iso);
  const minutes = now.diff(then, 'minute');
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = now.diff(then, 'hour');
  if (hours < 24) return `${hours}h ago`;
  return `${now.diff(then, 'day')}d ago`;
}

function describeAction(h: EnrolmentHistoryEntry): ReactNode {
  const network = () => NETWORK_LABEL[h.field ?? ''] ?? (h.field ?? '').toUpperCase();
  const status = (v: string | null) => (v ? STATUS_LABEL[v] ?? v : v);
  const strong = (text: ReactNode) => <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{text}</Box>;
  switch (h.action) {
    case 'created':
      return 'created the enrolment';
    case 'status_changed':
      return h.oldValue
        ? <>changed {strong(network())} from {status(h.oldValue)} to {strong(status(h.newValue))}</>
        : <>set {strong(network())} to {strong(status(h.newValue))}</>;
    case 'note_updated':
      if (!h.oldValue) return 'added a note';
      if (!h.newValue) return 'cleared the note';
      return 'updated the note';
    case 'updated': {
      if (h.field === 'brand_id') {
        const brandLabel = BRAND_LABEL.singular.toLowerCase();
        if (!h.newValue) return <>cleared the {strong(brandLabel)}</>;
        return h.oldValue
          ? <>changed {strong(brandLabel)} from {h.oldValue} to {strong(h.newValue)}</>
          : <>set {strong(brandLabel)} to {strong(h.newValue)}</>;
      }
      const label = BOOL_FIELD_LABEL[h.field ?? ''];
      if (label) return <>set {label} to {strong(h.newValue === 'true' ? 'Yes' : 'No')}</>;
      return 'updated the enrolment';
    }
    case 'verified':
      return <>verified {strong(network())}{h.newValue ? <> — {h.newValue}</> : null}</>;
    default:
      return 'updated the enrolment';
  }
}

// note_updated carries the new note text in newValue; show it in full below the line.
function noteDetail(h: EnrolmentHistoryEntry): string | null {
  return h.action === 'note_updated' && h.newValue ? h.newValue : null;
}

export default function EnrolmentHistoryDialog({ enrolment, onClose }: { enrolment: Enrolment | null; onClose: () => void }) {
  const { get, loading } = useApi();
  const [rows, setRows] = useState<EnrolmentHistoryEntry[]>([]);

  useEffect(() => {
    if (!enrolment) return;
    get<EnrolmentHistoryEntry[]>(`/api/alert-enrolments/${enrolment.id}/history`).then(setRows);
  }, [enrolment, get]);

  return (
    <Dialog open={Boolean(enrolment)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, pr: 1 }}>
        History — {enrolment?.ppAccountName}
        <IconButton aria-label="Close" onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={24} /></Box>
        ) : rows.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No history yet.</Box>
        ) : (
          <Box sx={{ py: 1 }}>
            {rows.map((h, i) => {
              const isLast = i === rows.length - 1;
              const color = actionColor(h);
              const note = noteDetail(h);
              return (
                <Box key={h.id} sx={{ display: 'flex', gap: 1.5 }}>
                  <Box sx={{ position: 'relative', width: 12, flexShrink: 0 }}>
                    <Box sx={{
                      position: 'absolute', top: 6, left: 1, width: 10, height: 10, borderRadius: '50%',
                      bgcolor: color, boxShadow: `0 0 0 3px ${color}22`,
                    }} />
                    {!isLast && (
                      <Box sx={{ position: 'absolute', top: 20, bottom: -8, left: 5, width: '2px', bgcolor: 'divider' }} />
                    )}
                  </Box>
                  <Box sx={{
                    flex: 1, minWidth: 0, pb: isLast ? 0.5 : 2.5,
                    display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2,
                  }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ fontSize: 14, lineHeight: 1.5, color: 'text.secondary' }}>
                        <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{formatActor(h.actorName)}</Box>{' '}
                        {describeAction(h)}
                      </Box>
                      {note && (
                        <Box sx={{ mt: 0.5, fontSize: 13, color: 'text.primary', whiteSpace: 'pre-wrap', wordBreak: 'break-word', borderLeft: '2px solid', borderColor: 'divider', pl: 1 }}>
                          {note}
                        </Box>
                      )}
                    </Box>
                    <Tooltip arrow placement="left" title={dayjs(h.createdAt).format('MMM D, YYYY, h:mm:ss A')}>
                      <Box component="span" sx={{ fontSize: 12, color: 'text.disabled', whiteSpace: 'nowrap', cursor: 'default' }}>
                        {relativeTime(h.createdAt)}
                      </Box>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
